import * as pes from '../crypto/pes1.js';

const MANIFEST_CHUNK = 1024 * 1024;

function putWithProgress(url, body, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded, e.total, 'upload');
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`上传失败：HTTP ${xhr.status} ${String(xhr.responseText || '').slice(0, 200)}`));
    };
    xhr.onerror = () => reject(new Error('上传失败：网络错误。请确认 R2 存储桶已配置 CORS 允许本站来源。'));
    xhr.send(body);
  });
}

export class Vault {
  constructor({ api, userId, keys, salt, kdf }) {
    this.api = api;
    this.userId = userId;
    this.keys = keys;
    this.salt = salt;
    this.kdf = kdf;
    this.manifestKey = pes.manifestObjectKey(userId);
  }

  async _getBytes(key) {
    const { url } = await this.api.signGet(key);
    const res = await fetch(url);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`下载失败：HTTP ${res.status}`);
    return new Uint8Array(await res.arrayBuffer());
  }

  async loadManifest() {
    const raw = await this._getBytes(this.manifestKey);
    if (!raw) return { schema: 1, revision: 0, updatedAt: null, entries: [] };
    const { plain } = pes.decryptBytes(raw, this.keys.manifestKey);
    const doc = JSON.parse(new TextDecoder().decode(plain));
    if (!Array.isArray(doc.entries)) doc.entries = [];
    return doc;
  }

  /** Re-reads the remote manifest, applies `updater`, and writes revision+1. */
  async commit(updater) {
    const current = await this.loadManifest();
    const entries = updater(current.entries.slice());
    const doc = {
      schema: 1,
      revision: (current.revision || 0) + 1,
      updatedAt: new Date().toISOString(),
      entries,
    };
    const enc = pes.encryptBytes(new TextEncoder().encode(JSON.stringify(doc)), this.keys.manifestKey, {
      salt: this.salt,
      kdf: this.kdf,
      chunkPlainSize: MANIFEST_CHUNK,
    });
    const { url } = await this.api.signPut(this.manifestKey);
    const res = await fetch(url, { method: 'PUT', body: enc.bytes });
    if (!res.ok) throw new Error(`保存索引失败：HTTP ${res.status}`);
    await res.arrayBuffer().catch(() => {});
    return doc;
  }

  async uploadFile(path, file, onProgress) {
    const objectId = await pes.pathToObjectId(this.keys.nameKey, path);
    const objectKey = pes.objectKeyFor(this.userId, objectId);
    const ids = pes.newContainerIds();

    // Encrypt chunk by chunk; the browser can spill the resulting Blob to disk,
    // so we never hold plaintext plus ciphertext plus the whole file in the JS heap.
    const parts = [];
    let ctSize = pes.HEADER_LEN;
    for await (const chunk of pes.encryptBlob(file, this.keys.fileKey, {
      salt: this.salt,
      kdf: this.kdf,
      fileNonce: ids.fileNonce,
      fileId: ids.fileId,
    })) {
      parts.push(chunk);
      ctSize += chunk.length;
      onProgress?.(ctSize, ctSize, 'encrypt');
    }
    const body = new Blob(parts, { type: 'application/octet-stream' });

    const { url } = await this.api.signPut(objectKey);
    await putWithProgress(url, body, onProgress);
    return {
      path: pes.normalizePath(path),
      kind: 'file',
      size: file.size,
      mtime: file.lastModified || Date.now(),
      objectKey,
      fileId: pes.toHex(ids.fileId),
      ctSize,
      createdAt: new Date().toISOString(),
    };
  }

  async downloadBlob(entry) {
    const { url } = await this.api.signGet(entry.objectKey);
    const res = await fetch(url);
    if (res.status === 404) throw new Error('对象不存在（可能已被删除）');
    if (!res.ok) throw new Error(`下载失败：HTTP ${res.status}`);
    const parts = [];
    for await (const chunk of pes.decryptStream(res.body, this.keys.fileKey)) {
      parts.push(chunk);
    }
    return new Blob(parts);
  }

  async deleteObject(entry) {
    if (!entry.objectKey) return;
    const { url } = await this.api.signDelete(entry.objectKey);
    const res = await fetch(url, { method: 'DELETE' });
    if (!res.ok && res.status !== 404) throw new Error(`删除失败：HTTP ${res.status}`);
    await res.arrayBuffer().catch(() => {});
  }
}

export const joinPath = (dir, name) => pes.normalizePath(`${dir}/${name}`);
export const parentPath = (path) => {
  const parts = pes.normalizePath(path).split('/').filter(Boolean);
  parts.pop();
  return '/' + parts.join('/');
};
export const baseName = (path) => pes.normalizePath(path).split('/').filter(Boolean).pop() || '/';

export function childrenOf(entries, dir) {
  const base = pes.normalizePath(dir);
  const prefix = base === '/' ? '/' : base + '/';
  const out = [];
  for (const e of entries) {
    if (!e.path.startsWith(prefix)) continue;
    const rest = e.path.slice(prefix.length);
    if (!rest) continue;
    const seg = rest.split('/')[0];
    const childPath = (base === '/' ? '' : base) + '/' + seg;
    if (out.some((c) => c.path === childPath)) continue;
    out.push(rest.includes('/') ? { path: childPath, kind: 'dir', synthetic: true, mtime: 0 } : { ...e, path: childPath });
  }
  return out.sort((a, b) => (a.kind === b.kind ? a.path.localeCompare(b.path) : a.kind === 'dir' ? -1 : 1));
}
